/**
 * ShopUI System
 * 
 * Handles the trading post shop interface including item display,
 * purchasing, and feedback animations.
 */

import Phaser from 'phaser';
import { Socket } from 'socket.io-client';

// ============ INTERFACES ============

export interface ShopItem {
  id: string;
  name: string;
  description: string;
  category: string;
  buyPrice: number;
  sellPrice: number;
  stock: number;
  icon: string;
}

export interface ShopData {
  id: string;
  name: string;
  inventory: ShopItem[];
}

// ============ SHOP UI SYSTEM ============

export class ShopUI {
  private scene: Phaser.Scene;
  private socket: Socket | null = null;
  private shopUI: Phaser.GameObjects.Container | null = null;
  private currentShopLocation: string | null = null;
  private shopData: ShopData | null = null;

  constructor(scene: Phaser.Scene) {
    this.scene = scene;
  }

  // ============ PUBLIC API ============

  public setSocket(socket: Socket | null): void {
    this.socket = socket;
  }

  public isOpen(): boolean {
    return this.shopUI !== null;
  }

  public getCurrentLocation(): string | null {
    return this.currentShopLocation;
  }

  public getShopData(): ShopData | null {
    return this.shopData;
  }

  /**
   * Open the shop UI - emit event to React component
   */
  public open(locationId: string, shopName: string): void {
    this.currentShopLocation = locationId;
    
    // Emit event to React component to open the ShopPanel
    this.scene.events.emit('tradingpost:interact', { name: shopName, locationId });
  }

  /**
   * Show shop loading state
   */
  public showLoading(shopName: string): void {
    if (this.shopUI) {
      this.shopUI.destroy();
    }
    
    const { width, height } = this.scene.cameras.main;
    
    this.shopUI = this.scene.add.container(0, 0);
    this.shopUI.setScrollFactor(0);
    this.shopUI.setDepth(1500);
    
    // Semi-transparent overlay
    const overlay = this.scene.add.rectangle(width / 2, height / 2, width, height, 0x000000, 0.6);
    this.shopUI.add(overlay);
    
    // Loading text
    const loadingText = this.scene.add.text(width / 2, height / 2, `Opening ${shopName}...`, {
      fontFamily: '"Sometype Mono", monospace',
      fontSize: '18px',
      color: '#b39ddb',
    });
    loadingText.setOrigin(0.5);
    this.shopUI.add(loadingText);
  }

  /**
   * Display the shop UI with inventory
   */
  public display(shop: ShopData): void {
    if (this.shopUI) {
      this.shopUI.destroy();
    }
    
    this.shopData = shop;
    const { width, height } = this.scene.cameras.main;
    
    this.shopUI = this.scene.add.container(0, 0);
    this.shopUI.setScrollFactor(0);
    this.shopUI.setDepth(1500);
    
    // Semi-transparent overlay (click to close)
    const overlay = this.scene.add.rectangle(width / 2, height / 2, width, height, 0x000000, 0.7);
    overlay.setInteractive();
    overlay.on('pointerdown', () => this.close());
    this.shopUI.add(overlay);
    
    // Shop panel - pastel purple theme
    const panelWidth = 400;
    const panelHeight = 450;
    const panelX = width / 2;
    const panelY = height / 2;
    
    const panel = this.scene.add.rectangle(panelX, panelY, panelWidth, panelHeight, 0x2d1b4e, 0.95);
    panel.setStrokeStyle(3, 0xb39ddb);
    this.shopUI.add(panel);
    
    // Shop title
    const title = this.scene.add.text(panelX, panelY - panelHeight / 2 + 25, `⚓ ${shop.name}`, {
      fontFamily: '"Sometype Mono", monospace',
      fontSize: '18px',
      color: '#d1c4e9',
      fontStyle: 'bold',
    });
    title.setOrigin(0.5);
    this.shopUI.add(title);
    
    // Close button
    const closeBtn = this.scene.add.text(panelX + panelWidth / 2 - 20, panelY - panelHeight / 2 + 15, '✕', {
      fontFamily: '"Sometype Mono", monospace',
      fontSize: '20px',
      color: '#e74c3c',
    });
    closeBtn.setOrigin(0.5);
    closeBtn.setInteractive({ useHandCursor: true });
    closeBtn.on('pointerdown', () => this.close());
    closeBtn.on('pointerover', () => closeBtn.setColor('#ff6b6b'));
    closeBtn.on('pointerout', () => closeBtn.setColor('#e74c3c'));
    this.shopUI.add(closeBtn);
    
    // Divider
    const divider = this.scene.add.rectangle(panelX, panelY - panelHeight / 2 + 50, panelWidth - 20, 2, 0x7e57c2);
    this.shopUI.add(divider);
    
    // Item list
    const itemStartY = panelY - panelHeight / 2 + 80;
    const itemHeight = 60;
    
    shop.inventory.forEach((item, index) => {
      this.createShopItemRow(item, index, panelX, panelWidth, itemStartY, itemHeight);
    });
    
    // Instructions
    const instructions = this.scene.add.text(panelX, panelY + panelHeight / 2 - 20, 'Click outside to close', {
      fontFamily: '"Sometype Mono", monospace',
      fontSize: '10px',
      color: '#888888',
    });
    instructions.setOrigin(0.5);
    this.shopUI.add(instructions);
  }

  /**
   * Buy an item from the shop
   */
  public buyItem(itemId: string): void {
    if (!this.currentShopLocation) return;
    
    this.socket?.emit('shop:buy', {
      locationId: this.currentShopLocation,
      itemId,
      quantity: 1,
    });
  }

  /**
   * Close the shop UI
   */
  public close(): void {
    if (this.shopUI) {
      this.shopUI.destroy();
      this.shopUI = null;
    }
    this.currentShopLocation = null;
    this.shopData = null;
    this.socket?.emit('shop:close');
  }

  /**
   * Show purchase feedback animation
   */
  public showPurchaseFeedback(cost: number): void {
    const { width } = this.scene.cameras.main;
    
    const text = this.scene.add.text(width / 2, 120, `-${cost.toLocaleString()} ₱`, {
      fontFamily: '"Sometype Mono", monospace',
      fontSize: '20px',
      color: '#e74c3c',
      fontStyle: 'bold',
      stroke: '#000000',
      strokeThickness: 3,
    });
    text.setOrigin(0.5);
    text.setScrollFactor(0);
    text.setDepth(2000);
    
    this.scene.tweens.add({
      targets: text,
      y: 80,
      alpha: 0,
      duration: 1500,
      ease: 'Sine.easeOut',
      onComplete: () => text.destroy(),
    });
  }

  // ============ PRIVATE METHODS ============

  private createShopItemRow(
    item: ShopItem, 
    index: number, 
    panelX: number, 
    panelWidth: number, 
    itemStartY: number, 
    itemHeight: number
  ): void {
    if (!this.shopUI) return;
    
    const itemY = itemStartY + index * itemHeight;
    
    // Item background (alternating)
    const itemBg = this.scene.add.rectangle(panelX, itemY + 20, panelWidth - 30, itemHeight - 5, 
      index % 2 === 0 ? 0x3d2b5e : 0x4d3b6e, 0.8);
    itemBg.setInteractive({ useHandCursor: true });
    this.shopUI.add(itemBg);
    
    // Item icon
    const icon = this.scene.add.text(panelX - panelWidth / 2 + 30, itemY + 20, item.icon, {
      fontSize: '24px',
    });
    icon.setOrigin(0.5);
    this.shopUI.add(icon);
    
    // Item name
    const nameText = this.scene.add.text(panelX - panelWidth / 2 + 60, itemY + 10, item.name, {
      fontFamily: '"Sometype Mono", monospace',
      fontSize: '12px',
      color: '#ffffff',
      fontStyle: 'bold',
    });
    this.shopUI.add(nameText);
    
    // Item description
    const descText = this.scene.add.text(panelX - panelWidth / 2 + 60, itemY + 26, item.description, {
      fontFamily: '"Sometype Mono", monospace',
      fontSize: '9px',
      color: '#b39ddb',
    });
    this.shopUI.add(descText);
    
    // Price
    const priceText = this.scene.add.text(panelX + panelWidth / 2 - 80, itemY + 12, `${item.buyPrice.toLocaleString()} ₱`, {
      fontFamily: '"Sometype Mono", monospace',
      fontSize: '11px',
      color: '#f39c12',
    });
    priceText.setOrigin(1, 0);
    this.shopUI.add(priceText);
    
    // Stock
    const stockText = item.stock === -1 ? '∞' : `${item.stock}`;
    const stockLabel = this.scene.add.text(panelX + panelWidth / 2 - 80, itemY + 28, `Stock: ${stockText}`, {
      fontFamily: '"Sometype Mono", monospace',
      fontSize: '9px',
      color: item.stock === 0 ? '#e74c3c' : '#888888',
    });
    stockLabel.setOrigin(1, 0);
    this.shopUI.add(stockLabel);
    
    // Buy button
    const buyBtn = this.scene.add.rectangle(panelX + panelWidth / 2 - 35, itemY + 20, 50, 25, 
      item.stock === 0 ? 0x555555 : 0x7e57c2);
    buyBtn.setStrokeStyle(1, 0xb39ddb);
    if (item.stock !== 0) {
      buyBtn.setInteractive({ useHandCursor: true });
      buyBtn.on('pointerover', () => buyBtn.setFillStyle(0x9575cd));
      buyBtn.on('pointerout', () => buyBtn.setFillStyle(0x7e57c2));
      buyBtn.on('pointerdown', () => this.buyItem(item.id));
    }
    this.shopUI.add(buyBtn);
    
    const buyText = this.scene.add.text(panelX + panelWidth / 2 - 35, itemY + 20, 'BUY', {
      fontFamily: '"Sometype Mono", monospace',
      fontSize: '10px',
      color: item.stock === 0 ? '#888888' : '#ffffff',
      fontStyle: 'bold',
    });
    buyText.setOrigin(0.5);
    this.shopUI.add(buyText);
  }
}
